import { useCallback, useState } from "react";
import { Box, Alert } from "@mui/material";
import { GoogleLogin } from "@react-oauth/google";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";

import axios from "../utils/axios";
import { setProfile } from "../reducers/profile";

export default function GoogleSignInButton({ onSuccess, onError, activityId = undefined, deferCreation = false }) {
  const dispatch = useDispatch();
  const [error, setError] = useState(null);

  const handleGoogleSuccess = useCallback(
    async (credentialResponse) => {
      try {
        setError(null);

        const credential = credentialResponse.credential;

        // In deferred mode (Sign Up), ask the backend NOT to create a new
        // account. It returns the verified Google identity for a brand-new user
        // (so the account is created only after onboarding completes) or logs in
        // an existing user. This prevents empty accounts when signup is abandoned.
        const response = await axios.post("auth/google", {
          credential,
          ...(deferCreation ? { defer_creation: true } : {}),
        });

        // Brand-new account, deferred: nothing was created. Hand the credential
        // to the caller to exchange at the final signup step.
        if (response.data.requires_registration) {
          if (onSuccess) {
            onSuccess({
              requiresProfileCompletion: true,
              deferred: true,
              credential,
              user: response.data.user,
            });
          }
          return;
        }

        // Existing user (either mode): log them in now.
        const token = response.data.access_token;
        if (!token) {
          throw new Error("No token returned from API");
        }

        localStorage.setItem("sva_webapp_token", token);

        // If joining an activity, do that now
        if (activityId) {
          await axios.get(`activities/join/${activityId}`, { withCredentials: true });
        }

        // Get user profile
        const profileResponse = await axios.get("auth/profile", { withCredentials: true });
        dispatch(setProfile(profileResponse.data.data));

        if (onSuccess) {
          onSuccess({
            requiresProfileCompletion: !!response.data.requires_profile_completion,
            user: response.data.user,
          });
        }
      } catch (err) {
        const errorData = err.response?.data;
        const errorMessage = errorData?.message || errorData?.errors?.credential?.[0] || "Google sign-in failed. Please try again.";
        setError(errorMessage);
        if (onError) {
          onError(errorMessage);
        }
      }
    },
    [dispatch, onSuccess, onError, activityId, deferCreation]
  );

  const handleGoogleError = useCallback(() => {
    const errorMessage = "Google sign-in was cancelled or failed";
    setError(errorMessage);
    if (onError) {
      onError(errorMessage);
    }
  }, [onError]);

  return (
    <Box>
      <Box display="flex" justifyContent="center" width="100%">
        <GoogleLogin
          onSuccess={handleGoogleSuccess}
          onError={handleGoogleError}
          useOneTap={false}
          size="large"
          text="continue_with"
          shape="rectangular"
        />
      </Box>
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}

GoogleSignInButton.propTypes = {
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  activityId: PropTypes.number,
  deferCreation: PropTypes.bool,
};
